import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip } from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import PauseCircleFilledIcon from '@material-ui/icons/PauseCircleFilled';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import SpeedIcon from '@material-ui/icons/Speed';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import StorageIcon from '@material-ui/icons/Storage';
import FlightIcon from '@material-ui/icons/Flight';
import CloseIcon from '@material-ui/icons/Close';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import { EmptyCollection, PageLoader, Pagination } from '../../../../shared_elements';
import { STableLoader } from '../../../../shared_elements/loaders';
import { getFileSize, getLocalStorageInfo } from '../../../../utils';
import { getUploadSessionsApi, getUploadSessionFilesApi, updateUploadFileStatusApi, completeUploadSessionApi, updateUploadSessionTotalsApi, getErrorMessage } from '../apiServices';
import { SESSION_STATUS, sessionStatusConfig } from '../constants';
import { useLiveUploads, useOnlineStatus, getLockedUploadIds, withUploadLock, isRecentActivity, UNFINISHED_FILE_STATUSES, LIVE_STATUS } from '../liveUploads';
import { runStreamUpload, runPool, describeUploadResult, pauseStreamUpload, resumeStreamUpload } from '../uploadEngine';
import StreamUploadModal from './StreamUploadModal';
import SessionFilesDialog from './SessionFilesDialog';
import { folderLink, getConfig, formatDuration, formatTime, StatusChip } from './sessionDisplay';

const POLL_INTERVAL_MS = 5000;
const DEFAULT_PER_PAGE = 20;
const SNACKBAR_POSITION = { vertical: 'top', horizontal: 'right' };
const FINISHED_FILE_STATUSES = ['COMPLETED', 'UPLOADED', 'FAILED'];
const FINISH_CONCURRENCY = 6;
// How often a quiet session's UPLOADING files are re-read for activity
const FILE_ACTIVITY_RECHECK_MS = 30 * 1000;
const PAGINATION_HEIGHT = 52;
const TABLE_BOTTOM_GAP = 24;
const MIN_TABLE_HEIGHT = 240;

const stopRowClick = (evt) => evt.stopPropagation();

const getCurrentUserId = () => {
  const info = getLocalStorageInfo();
  return info && info.user ? info.user.id : null;
};

const isOwnSession = (session, currentUserId) =>
  !!session.created_by && currentUserId !== null && String(session.created_by.id) === String(currentUserId);

const latestUpdate = (files) => files.reduce((latest, file) => (file.updatedAt && (!latest || file.updatedAt > latest) ? file.updatedAt : latest), null);

const RelativeTime = ({ value }) => value ?
  <Tooltip arrow title={formatTime(value)}>
    <span className="upload-progress__muted">{moment(value).fromNow()}</span>
  </Tooltip> : <span className="upload-progress__muted">--</span>;

const SessionProgress = ({ session }) => {
  const config = getConfig(session.status);
  const percentage = Math.min(100, session.progress_percentage || 0);
  return (
    <div className="upload-progress__progress-cell">
      <div className="upload-progress__progress-row">
        <LinearProgress
          variant="determinate"
          value={percentage}
          className="upload-progress__progress-bar"
          classes={{ bar: 'upload-progress__progress-bar-fill' }}
          style={{ '--bar-color': config.bar }}
        />
        <span>{Math.floor(percentage)}%</span>
      </div>
      <div className="upload-progress__progress-note">
        {session.isPreparing ?
          `Preparing ${(session.registeredFiles || 0).toLocaleString()} of ${(session.total_files || 0).toLocaleString()} files` :
          `${getFileSize(session.uploaded_bytes)} of ${getFileSize(session.total_bytes)}`}
        {session.isLive && !session.isPreparing && session.liveSpeed > 0 ? ` · ${getFileSize(session.liveSpeed)}/s` : ''}
        {session.uploadingElsewhere ? ' · --' : ''}
      </div>
    </div>
  );
};

const ProgressRing = ({ value, done }) => (
  <div className="upload-progress__ring">
    <CircularProgress variant="determinate" value={100} size={72} thickness={4.5} className="upload-progress__ring-layer upload-progress__ring-track" />
    <CircularProgress variant="determinate" value={value} size={72} thickness={4.5} className="upload-progress__ring-layer" style={{ color: done ? sessionStatusConfig.COMPLETED.bar : sessionStatusConfig.ACTIVE.bar }} />
    <div className="upload-progress__ring-label">{Math.floor(value)}%</div>
  </div>
);

const Metric = ({ icon, label, value }) => (
  <div className="upload-progress__metric">
    {icon}
    <div><span>{label}</span><strong>{value}</strong></div>
  </div>
);

const sumBy = (list, key) => list.reduce((sum, item) => sum + (Number(item[key]) || 0), 0);

// Overlays this tab's live numbers (never lower than the server's)
const withLiveProgress = (session, live, lockedIds, fileActivity) => {
  if (!live) {
    if (session.status !== SESSION_STATUS.ACTIVE) return session;
    // Not in this tab: locked by another tab or recently active means uploading elsewhere, otherwise interrupted
    if (lockedIds.has(session.upload_id)) return { ...session, uploadingElsewhere: 'tab' };
    if (isRecentActivity(session.updated_at) || isRecentActivity(fileActivity[session.upload_id])) return { ...session, uploadingElsewhere: 'device' };
    return { ...session, status: SESSION_STATUS.INTERRUPTED };
  }
  const totalBytes = session.total_bytes || live.totalBytes || 0;
  const uploadedBytes = Math.max(session.uploaded_bytes || 0, live.uploadedBytes || 0);
  const completedFiles = Math.max(session.completed_files || 0, live.completedFiles || 0);
  let status = live.status === LIVE_STATUS.PAUSED ? SESSION_STATUS.PAUSED : live.preparing ? SESSION_STATUS.PREPARING : SESSION_STATUS.ACTIVE;
  if (live.status === LIVE_STATUS.DONE) {
    // Run ended: completed if nothing failed, otherwise interrupted (resumable)
    if (!live.failedFiles && !live.skippedFiles) status = SESSION_STATUS.COMPLETED;
    else status = session.status === SESSION_STATUS.ACTIVE ? SESSION_STATUS.INTERRUPTED : session.status;
  }
  return {
    ...session,
    completed_files: completedFiles,
    failed_files: Math.max(session.failed_files || 0, live.failedFiles || 0),
    uploaded_bytes: uploadedBytes,
    progress_percentage: totalBytes ? (uploadedBytes / totalBytes) * 100 : session.progress_percentage,
    status,
    liveSpeed: live.status === LIVE_STATUS.UPLOADING ? live.speed : 0,
    isLive: live.status === LIVE_STATUS.UPLOADING,
    isPaused: live.status === LIVE_STATUS.PAUSED,
    isPreparing: live.status === LIVE_STATUS.UPLOADING && !!live.preparing,
    registeredFiles: live.registeredFiles || 0,
  };
};

const UploadProgress = () => {
  const [skeletonLoader, setSkeletonLoader] = useState(true);
  const [sessions, setSessions] = useState({ list: [], pagination: null, lockedIds: new Set(), fileActivity: {} });
  // upload_id -> { checkedAt, latest } activity of its UPLOADING files
  const fileActivityCache = useRef({});
  const [lastUpdated, setLastUpdated] = useState(null);
  const [pageLoader, setPageLoader] = useState(false);
  const lastQuery = useRef({ page: 1, per_page: DEFAULT_PER_PAGE });
  const [resumeTarget, setResumeTarget] = useState(null);
  const [resumeFiles, setResumeFiles] = useState(null);
  const resumeRequest = useRef(0);
  const [finishTarget, setFinishTarget] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [filesTargetId, setFilesTargetId] = useState(null);
  const [currentUserId] = useState(getCurrentUserId);
  const tableScrollRef = useRef(null);
  const [tableMaxHeight, setTableMaxHeight] = useState(MIN_TABLE_HEIGHT);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Fit the table to the window, leaving room for the pagination bar
  const fitTable = useCallback(() => {
    if (!tableScrollRef.current) return;
    const top = tableScrollRef.current.getBoundingClientRect().top;
    const height = Math.max(MIN_TABLE_HEIGHT, Math.floor(window.innerHeight - top - PAGINATION_HEIGHT - TABLE_BOTTOM_GAP));
    setTableMaxHeight(current => (Math.abs(current - height) > 1 ? height : current));
  }, []);
  useLayoutEffect(fitTable);
  useEffect(() => {
    window.addEventListener('resize', fitTable);
    return () => window.removeEventListener('resize', fitTable);
  }, [fitTable]);
  // The app's SnackbarProvider adds no close button to persistent toasts
  const dismissAction = (key) => <IconButton size="small" color="inherit" onClick={() => closeSnackbar(key)}><CloseIcon fontSize="small" /></IconButton>;

  const getUploadSessions = useCallback((params) => {
    lastQuery.current = params;
    return Promise.all([getUploadSessionsApi(params), getLockedUploadIds()]).then(async ([response, lockedIds]) => {
      if (!response.ok) {
        setSkeletonLoader(false);
        return;
      }
      // A quiet session may still have a large file uploading, so its UPLOADING files' activity decides
      const checkedAt = Date.now();
      const quiet = response.list.filter(session => session.status === SESSION_STATUS.ACTIVE && session.asset_slug &&
        !lockedIds.has(session.upload_id) && !isRecentActivity(session.updated_at));
      const due = quiet.filter(session => {
        const cached = fileActivityCache.current[session.upload_id];
        return !cached || checkedAt - cached.checkedAt > FILE_ACTIVITY_RECHECK_MS;
      });
      await Promise.all(due.map(session =>
        getUploadSessionFilesApi({ assetType: session.asset_type, assetSlug: session.asset_slug, uploadId: session.upload_id, status: 'UPLOADING' })
          .then(files => { fileActivityCache.current[session.upload_id] = { checkedAt, latest: latestUpdate(files) }; })
          .catch(() => {})
      ));
      const fileActivity = {};
      Object.keys(fileActivityCache.current).forEach(id => { fileActivity[id] = fileActivityCache.current[id].latest; });
      setSkeletonLoader(false);
      setSessions({ list: response.list, pagination: response.pagination, lockedIds, fileActivity });
      setLastUpdated(Date.now());
    });
  }, []);

  useEffect(() => {
    getUploadSessions({ page: 1, per_page: DEFAULT_PER_PAGE });
  }, [getUploadSessions]);

  const changePage = (params) => {
    setPageLoader(true);
    getUploadSessions(params).catch(() => {}).finally(() => setPageLoader(false));
  };

  const live = useLiveUploads();
  const online = useOnlineStatus();
  const { pagination } = sessions;
  const list = sessions.list.map(session => withLiveProgress(session, live[session.upload_id], sessions.lockedIds, sessions.fileActivity));
  const isActive = list.some(session => [SESSION_STATUS.ACTIVE, SESSION_STATUS.PREPARING, SESSION_STATUS.INTERRUPTED].includes(session.status));
  // Sessions counted in the header: uploading anywhere or paused here
  const uploadingNow = list.filter(session => session.isLive || session.uploadingElsewhere || session.isPaused);
  const pausedNow = list.filter(session => session.isPaused);
  const sendingCount = uploadingNow.length - pausedNow.length;
  const interrupted = list.filter(session => session.status === SESSION_STATUS.INTERRUPTED);
  const liveUploading = Object.values(live).filter(upload => upload.status === LIVE_STATUS.UPLOADING);
  const liveSpeed = sumBy(liveUploading, 'speed');
  const liveRemainingBytes = liveUploading.reduce((sum, upload) => sum + Math.max(0, (upload.totalBytes || 0) - (upload.uploadedBytes || 0)), 0);

  // Fetch the list again when this tab starts a session it doesn't have yet
  const fetchedLiveIds = useRef(new Set());
  const liveIds = Object.keys(live).join(',');
  // Refresh when a run here ends so the server's final status shows
  const liveDoneIds = Object.values(live).filter(upload => upload.status === LIVE_STATUS.DONE).map(upload => upload.uploadId).join(',');
  useEffect(() => {
    if (liveDoneIds && !skeletonLoader) getUploadSessions(lastQuery.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveDoneIds]);
  useEffect(() => {
    const missing = Object.keys(live).filter(id => !fetchedLiveIds.current.has(id) && !sessions.list.some(session => session.upload_id === id));
    if (!missing.length || skeletonLoader) return;
    missing.forEach(id => fetchedLiveIds.current.add(id));
    getUploadSessions(lastQuery.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveIds, skeletonLoader]);

  useEffect(() => {
    if (!isActive) return undefined;
    const timer = setInterval(() => getUploadSessions(lastQuery.current), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isActive, getUploadSessions]);

  const perPage = pagination ? pagination.per_page : DEFAULT_PER_PAGE;
  const filesTarget = filesTargetId ? list.find(session => session.upload_id === filesTargetId) : null;

  const sessionApi = (session) => ({ assetType: session.asset_type, assetSlug: session.asset_slug, uploadId: session.upload_id });

  const loadResumeFiles = (session) => {
    const request = ++resumeRequest.current;
    setResumeFiles({ loading: true, files: null, error: null });
    getUploadSessionFilesApi(sessionApi(session))
      .then(files => { if (resumeRequest.current === request) setResumeFiles({ loading: false, files, error: null }); })
      .catch(error => {
        if (resumeRequest.current === request) setResumeFiles({ loading: false, files: null, error: error.message });
      });
  };

  const openResume = (session) => {
    setResumeTarget(session);
    loadResumeFiles(session);
  };

  const closeResume = () => {
    resumeRequest.current += 1;
    setResumeTarget(null);
    setResumeFiles(null);
  };

  const resumeUpload = async (items, { adjustTotals } = {}) => {
    const session = resumeTarget;
    closeResume();
    if (adjustTotals) {
      try {
        await updateUploadSessionTotalsApi({ ...sessionApi(session), ...adjustTotals });
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'Could not update the upload before resuming. Check your connection and try again.'), { variant: 'error', anchorOrigin: SNACKBAR_POSITION });
        return;
      }
    }
    enqueueSnackbar(`Resuming upload of ${items.length.toLocaleString()} file${items.length > 1 ? 's' : ''}`, { variant: 'info', anchorOrigin: SNACKBAR_POSITION });
    runStreamUpload({ ...sessionApi(session), items })
      .then(result => {
        const { message, variant } = describeUploadResult(result);
        enqueueSnackbar(message, { variant, persist: variant !== 'success', action: dismissAction, anchorOrigin: SNACKBAR_POSITION });
        getUploadSessions(lastQuery.current);
      });
  };

  // Finishing early: mark every unfinished file FAILED, then the session COMPLETED
  const finishSession = () => {
    const session = finishTarget;
    const api = sessionApi(session);
    setFinishing(true);
    // Hold the lock so another tab can't resume it meanwhile
    withUploadLock(session.upload_id, async () => {
      const files = await getUploadSessionFilesApi(api);
      const unfinished = files.filter(file => !FINISHED_FILE_STATUSES.includes(file.status));
      if (unfinished.some(file => UNFINISHED_FILE_STATUSES.includes(file.status) && isRecentActivity(file.updatedAt))) return 'active-elsewhere';
      await runPool(unfinished.map(file => () => updateUploadFileStatusApi({ ...api, fileId: file.fileId, status: 'FAILED', errorMessage: 'Not uploaded: upload finished without this file' })), FINISH_CONCURRENCY);
      await completeUploadSessionApi(api);
      return 'finished';
    }, () => 'active-in-tab')
      .then(outcome => {
        if (outcome === 'finished') {
          enqueueSnackbar('Upload finished. Files that were not uploaded are marked as failed.', { variant: 'success', anchorOrigin: SNACKBAR_POSITION });
        } else if (outcome === 'active-in-tab') {
          enqueueSnackbar('This upload is running in another tab, so it can\'t be finished here', { variant: 'warning', anchorOrigin: SNACKBAR_POSITION });
        } else {
          enqueueSnackbar('Files in this upload were active in the last minute, so it may still be running on another device. Try again later.', { variant: 'warning', anchorOrigin: SNACKBAR_POSITION });
        }
        setFinishTarget(null);
      })
      .catch(error => {
        enqueueSnackbar(getErrorMessage(error, 'Could not finish the upload. Check your connection and try again.'), { variant: 'error', anchorOrigin: SNACKBAR_POSITION });
      })
      .finally(() => {
        setFinishing(false);
        getUploadSessions(lastQuery.current);
      });
  };

  const totalFiles = sumBy(uploadingNow, 'total_files');
  const completedFiles = sumBy(uploadingNow, 'completed_files');
  const failedFiles = sumBy(uploadingNow, 'failed_files');
  const totalBytes = sumBy(uploadingNow, 'total_bytes');
  const uploadedBytes = sumBy(uploadingNow, 'uploaded_bytes');
  const percentage = totalBytes ? Math.min(100, (uploadedBytes / totalBytes) * 100) : 0;
  const done = totalFiles > 0 && completedFiles === totalFiles;
  let speedLabel = '--';
  let etaLabel = '--';
  if (liveUploading.length && !online) {
    speedLabel = 'Waiting for network';
  } else if (liveUploading.length && liveUploading.every(upload => upload.preparing)) {
    speedLabel = 'Preparing...';
    etaLabel = 'Preparing...';
  } else if (liveUploading.length) {
    speedLabel = liveSpeed > 0 ? `${getFileSize(liveSpeed)}/s` : 'Calculating...';
    etaLabel = (liveSpeed > 0 && liveRemainingBytes > 0 && formatDuration(liveRemainingBytes / liveSpeed)) || 'Calculating...';
  }

  return (
    <div className="upload-progress">
      {pageLoader ? <PageLoader /> : null}
      {list.length ?
        <Paper className="upload-progress__header">
          <div className="upload-progress__header-top">
            {uploadingNow.length ? <ProgressRing value={percentage} done={done} /> : null}
            <div className="upload-progress__headline">
              <div className="upload-progress__title">
                {liveUploading.length && !online ?
                  <><WarningIcon style={{ color: sessionStatusConfig.INTERRUPTED.color }} />No network connection. Uploads will continue when you're back online</> :
                  sendingCount && list.filter(session => session.isPreparing).length === sendingCount ?
                  <><CloudUploadIcon style={{ color: sessionStatusConfig.ACTIVE.color }} />Preparing {sendingCount} upload{sendingCount > 1 ? 's' : ''}{pausedNow.length ? ` · ${pausedNow.length} paused` : ''}</> :
                  sendingCount ?
                  <><CloudUploadIcon style={{ color: sessionStatusConfig.ACTIVE.color }} />{sendingCount} upload{sendingCount > 1 ? 's' : ''} in progress{pausedNow.length ? ` · ${pausedNow.length} paused` : ''}</> :
                  pausedNow.length ?
                  <><PauseCircleFilledIcon style={{ color: sessionStatusConfig.PAUSED.color }} />{pausedNow.length} upload{pausedNow.length > 1 ? 's' : ''} paused</> :
                  interrupted.length ?
                  <><WarningIcon style={{ color: sessionStatusConfig.INTERRUPTED.color }} />{interrupted.length} upload{interrupted.length > 1 ? 's' : ''} interrupted</> :
                    <><CheckCircleIcon style={{ color: sessionStatusConfig.COMPLETED.color }} />No uploads in progress</>
                }
              </div>
              {uploadingNow.length ?
                <div className="upload-progress__subtitle">
                  <b>{completedFiles.toLocaleString()}</b> of <b>{totalFiles.toLocaleString()}</b> files completed
                  {totalFiles > completedFiles ? <> · <b>{(totalFiles - completedFiles).toLocaleString()}</b> remaining</> : null}
                  {failedFiles ? <> · <b style={{ color: sessionStatusConfig.FAILED.color }}>{failedFiles.toLocaleString()}</b> failed</> : null}
                </div> : null
              }
            </div>
            {uploadingNow.length ?
              <div className="upload-progress__metrics">
                <Metric icon={<StorageIcon />} label="Uploaded" value={`${getFileSize(uploadedBytes)} / ${getFileSize(totalBytes)}`} />
                <Metric icon={<SpeedIcon />} label="Speed" value={speedLabel} />
                <Metric icon={<HourglassEmptyIcon />} label="Time left" value={etaLabel} />
              </div> : null
            }
          </div>
        </Paper> : null
      }
      <Paper>
        <div className="upload-progress__list-header">
          <div className="upload-progress__list-title">
            Upload Sessions
            {pagination ? <span>{pagination.total.toLocaleString()}</span> : null}
          </div>
          <div className="upload-progress__refresh-info">
            {lastUpdated ? `Updated ${moment(lastUpdated).format('hh:mm:ss A')}` : ''}
            <Tooltip title="Refresh" arrow>
              <IconButton size="small" onClick={() => getUploadSessions(lastQuery.current)}><RefreshIcon fontSize="small" /></IconButton>
            </Tooltip>
          </div>
        </div>
        {skeletonLoader ? <STableLoader count={7} size={6} /> :
          <>
            <div ref={tableScrollRef} style={{ maxHeight: !list.length ? 'auto' : `${tableMaxHeight}px`, overflow: 'auto' }}>
              <Table className="mui-table-format" stickyHeader={true} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Asset</TableCell>
                    <TableCell>Files</TableCell>
                    <TableCell>Progress</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Started</TableCell>
                    <TableCell>Created By</TableCell>
                    <TableCell>Last Updated</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {list.map(session => {
                    const link = folderLink(session);
                    return (
                    <TableRow
                      hover
                      tabIndex={0}
                      key={session.upload_id}
                      className="upload-progress__row upload-progress__row--clickable"
                      title="Show uploading and failed files"
                      onClick={() => setFilesTargetId(session.upload_id)}
                      onKeyDown={(evt) => { if (evt.key === 'Enter') setFilesTargetId(session.upload_id); }}
                    >
                      <TableCell>
                        <div className="upload-progress__asset-cell">
                          <div className="upload-progress__asset-icon"><FlightIcon /></div>
                          <div>
                            <div className="upload-progress__asset-name">{session.asset_name || '--'}</div>
                            {link ?
                              <Link to={link} className="upload-progress__folder-link" onClick={stopRowClick}>
                                <FolderOpenIcon /> Open folder
                              </Link> : null
                            }
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="upload-progress__files-count"><b>{(session.completed_files || 0).toLocaleString()}</b> / {(session.total_files || 0).toLocaleString()}</div>
                        {session.failed_files ?
                          <div className="upload-progress__sub-text" style={{ color: sessionStatusConfig.FAILED.color }}>{session.failed_files.toLocaleString()} failed</div> :
                          <div className="upload-progress__sub-text">completed</div>
                        }
                      </TableCell>
                      <TableCell><SessionProgress session={session} /></TableCell>
                      <TableCell>
                        <StatusChip status={session.status} />
                        {session.isLive || session.isPaused ?
                          <div className="upload-progress__resume-actions" onClick={stopRowClick}>
                            {session.isLive ?
                              <Tooltip arrow title="Stop sending for now. Files already uploaded are kept, and nothing has to be selected again to continue">
                                <Button size="small" className="upload-progress__resume-button" onClick={() => pauseStreamUpload(session.upload_id)}>Pause</Button>
                              </Tooltip> :
                              <Tooltip arrow title="Continue uploading from where it stopped">
                                <Button size="small" color="primary" className="upload-progress__resume-button" onClick={() => resumeStreamUpload(session.upload_id)}>Resume</Button>
                              </Tooltip>
                            }
                          </div> : null
                        }
                        {session.uploadingElsewhere ?
                          <div className="upload-progress__sub-text upload-progress__elsewhere">
                            {session.uploadingElsewhere === 'tab' ? 'Uploading in another tab' :
                              isOwnSession(session, currentUserId) ? 'Uploading on another device' :
                              `Uploading by ${(session.created_by && session.created_by.name) || 'another user'}`}
                          </div> : null
                        }
                        {session.status === SESSION_STATUS.INTERRUPTED && !isOwnSession(session, currentUserId) ?
                          <div className="upload-progress__sub-text upload-progress__elsewhere">Started by {(session.created_by && session.created_by.name) || 'another user'}</div> : null
                        }
                        {session.status === SESSION_STATUS.INTERRUPTED && isOwnSession(session, currentUserId) ?
                          <div className="upload-progress__resume-actions" onClick={stopRowClick}>
                            <Tooltip arrow title={session.asset_slug ? 'Select the same files again to finish this upload' : 'This upload is missing its asset details and cannot be resumed'}>
                              <span>
                                <Button size="small" color="primary" className="upload-progress__resume-button" disabled={!session.asset_slug} onClick={() => openResume(session)}>Resume</Button>
                              </span>
                            </Tooltip>
                            <Tooltip arrow title={session.asset_slug ? 'Stop here and keep only the files already uploaded' : 'This upload is missing its asset details and cannot be finished'}>
                              <span>
                                <Button size="small" className="upload-progress__resume-button" disabled={!session.asset_slug} onClick={() => setFinishTarget(session)}>Finish</Button>
                              </span>
                            </Tooltip>
                          </div> : null
                        }
                      </TableCell>
                      <TableCell><RelativeTime value={session.created_at} /></TableCell>
                      <TableCell>{(session.created_by && session.created_by.name) || '--'}</TableCell>
                      <TableCell><RelativeTime value={session.completed_at || session.updated_at} /></TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <Pagination
              pagination={pagination}
              onChangePage={(event, newPage) => changePage({ page: newPage + 1, per_page: perPage })}
              onChangeRowsPerPage={(event) => changePage({ page: 1, per_page: event.target.value })}
            />
            {!list.length ? <EmptyCollection title="No upload sessions yet" /> : null}
          </>
        }
      </Paper>
      {filesTarget ?
        <SessionFilesDialog
          session={filesTarget}
          live={live[filesTarget.upload_id]}
          onClose={() => setFilesTargetId(null)}
        /> : null
      }
      {resumeTarget ?
        <StreamUploadModal
          open={true}
          resumeSession={resumeTarget}
          resumeFiles={resumeFiles}
          onRetryResumeFiles={() => loadResumeFiles(resumeTarget)}
          onClose={closeResume}
          onStartUpload={resumeUpload}
        /> : null
      }
      {finishTarget ?
        <Dialog open={true} onClose={finishing ? undefined : () => setFinishTarget(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Finish Upload Without Remaining Files?</DialogTitle>
          <DialogContent dividers>
            <b>{(finishTarget.completed_files || 0).toLocaleString()}</b> of <b>{(finishTarget.total_files || 0).toLocaleString()}</b> files
            {finishTarget.asset_name ? <> for <b>{finishTarget.asset_name}</b></> : null} are uploaded and stay in the folder.
            The other files will be marked as failed and this upload can no longer be resumed.
          </DialogContent>
          <DialogActions>
            <Button color="primary" disabled={finishing} onClick={() => setFinishTarget(null)}>Cancel</Button>
            <Button color="primary" variant="contained" disabled={finishing} onClick={finishSession}>{finishing ? 'Finishing...' : 'Finish Upload'}</Button>
          </DialogActions>
        </Dialog> : null
      }
    </div>
  );
};

export default UploadProgress;
